import type { ComponentChildren } from 'preact';

import { cx, memberStyle } from './color.js';
import styles from './Progress.module.css';

function ratio(value: number, max: number): number {
  if (!(max > 0)) return 0;
  const r = value / max;
  return r < 0 ? 0 : r > 1 ? 1 : r;
}

export interface ProgressBarProps {
  value: number;
  max: number;
  /** Voorgelezen tekst, bv. "3 van 5 klaar" (uit i18n). */
  label: string;
  /** Lidkleur (volle tint op de lidtint); zonder: rustig accentblauw. */
  colorKey?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Toon het label ook zichtbaar rechts van de balk. */
  showLabel?: boolean;
}

/** Voortgangsbalk (taken per lid, doel-balk voor beloningen, TSK-54). Breedte via transform. */
export function ProgressBar({
  value,
  max,
  label,
  colorKey,
  size = 'md',
  showLabel,
}: ProgressBarProps) {
  const r = ratio(value, max);
  return (
    <div
      class={cx(styles.barWrap, styles[size])}
      style={colorKey ? memberStyle(colorKey) : undefined}
    >
      <div
        class={styles.track}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-label={label}
      >
        <div class={cx(styles.fill, r >= 1 && styles.complete)} style={`width:${r * 100}%`} />
      </div>
      {showLabel ? (
        <span class={styles.barLabel} aria-hidden="true">
          {label}
        </span>
      ) : null}
    </div>
  );
}

export interface ProgressRingProps {
  value: number;
  max: number;
  label: string;
  colorKey?: string;
  /** Buitenmaat in px (ledenrail: 100). */
  size?: number;
  /** Dikte van de ring in px. */
  thickness?: number;
  /** Inhoud in het midden (meestal een Avatar). */
  children?: ComponentChildren;
}

/** Voortgangsring rond een avatar op de ledenrail (ONTWERP.md §7.1). */
export function ProgressRing({
  value,
  max,
  label,
  colorKey,
  size = 100,
  thickness = 6,
  children,
}: ProgressRingProps) {
  const r = ratio(value, max);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const c = size / 2;
  return (
    <span
      class={styles.ring}
      style={`${colorKey ? memberStyle(colorKey) : ''};width:${size}px;height:${size}px`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-label={label}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
        focusable="false"
      >
        <circle
          class={styles.ringTrack}
          cx={c}
          cy={c}
          r={radius}
          stroke-width={thickness}
          fill="none"
        />
        {r > 0 ? (
          <circle
            class={cx(styles.ringFill, r >= 1 && styles.complete)}
            cx={c}
            cy={c}
            r={radius}
            stroke-width={thickness}
            fill="none"
            stroke-linecap="round"
            stroke-dasharray={`${circumference} ${circumference}`}
            stroke-dashoffset={circumference * (1 - r)}
            transform={`rotate(-90 ${c} ${c})`}
          />
        ) : null}
      </svg>
      {children ? <span class={styles.ringCenter}>{children}</span> : null}
    </span>
  );
}
